// src/features/messages/messagesSlice.ts
import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { Message } from "../models/Message";
import { fetchNewMessages } from "../services/messageService";
import { RootState } from "./store";
import { MessagePart, parseMessage } from "../utils/messageUtils";
import axios from "../utils/axios";
import { base64ToUint8Array, decryptMessage, encryptMessage } from "../services/crypto";
import { combineAndDecryptPrivateKey, decryptString } from "../utils/security";
import { shortenAddress } from "../utils/addressUtils";
import { Contact } from "../models/Contact";
import { MessageDirection } from "../types/messageDirection";

interface LoadingStatus {
  loading: boolean;
  error: string;
}
interface MessagesState {
  messages: Message[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  attachmentDrawerOpen?: boolean;
  messageParts: MessagePart[];
  fetchMessagesByContact: LoadingStatus,
  sendStatus: LoadingStatus
}

interface SendMessagePayload {
  content: string;
  connection: any;
  receiversPublicKey: string;
  privateKey: string | null;
  publicKey: string | null;
  selectedContact: Contact | null;
  messageParts: MessagePart[];
  walletAddress: string;
}

const initialState: MessagesState = {
  messages: [],
  status: "idle",
  error: null,
  attachmentDrawerOpen: false,
  messageParts: [],
  sendStatus: {
    loading: false,
    error: "",
  },
  fetchMessagesByContact: {
    loading: false,
    error: "",
  }
};

export const sendNewMessage = createAsyncThunk(
  "messages/sendNewMessage",
  async (payload: SendMessagePayload, { rejectWithValue, dispatch }) => {
    const {
      content,
      connection,
      receiversPublicKey,
      privateKey,
      publicKey,
      selectedContact,
      messageParts,
      walletAddress
    } = payload;

    if (!privateKey || !publicKey) {
      return rejectWithValue("Encryption keys not available");
    }

    try {
      // Create temporary message for optimistic update
      const tempMessage: Message = {
        id: Date.now().toString(),
        content,
        timestamp: new Date().toISOString(),
        direction: "outgoing" as MessageDirection,
        status: "sending",
        sender: walletAddress,
        parts: messageParts,
      };

      // Optimistic update
      dispatch(addMessage(tempMessage));

      // Encrypt the message
      const encrypted = await encryptMessage(
        base64ToUint8Array(receiversPublicKey),
        content,
        base64ToUint8Array(privateKey),
        base64ToUint8Array(publicKey)
      );

      // Send via SignalR connection
      await connection.invoke(
        "SendEncryptedMessage",
        selectedContact?.walletAddress || "",
        encrypted.ciphertext,
        encrypted.nonce,
        selectedContact?.isGroup ? selectedContact.groupId : null,
        1
      );

      // Return final message for state update
      return {
        ...tempMessage,
        status: "sent" as const,
        nonce: encrypted.nonce,
      };
    } catch (error) {
      console.error("Message send failed:", error);
      return rejectWithValue({
        message: "Failed to send message",
        code: "MESSAGE_SEND_ERROR",
        tempMessageId: Date.now().toString(),
      });
    }
  }
);



export const fetchMessages = createAsyncThunk(
  "messages/fetchMessages",
  async (groupId: string, { getState }) => {
    const currentMessages = (getState() as RootState).messages.messages;
    return await fetchNewMessages({ groupId }, currentMessages);
  }
);

export const fetchMessagesByContact = createAsyncThunk(
  "messages/fetchMessagesByContact",
  async (payload: { contact: string }, { rejectWithValue, getState }) => {
    try {
      const userState = (getState() as RootState).user;
      const contactsState = (getState() as RootState).contacts;
      const uiState = (getState() as RootState).ui;
      const authState = (getState() as RootState).auth;

      const {
        walletAddress: currentUserWalletAddress,
        publicKey: currentUserPublicKey,
        receiversPublicKey,
        user
      } = userState;

      const response = await axios.get(
        `api/services/app/Message/GetAllMessagesForContact?contact=${payload.contact}`
      );

      if (!response.data?.result?.items) {
        return [];
      }

      if (response.data.result.items.length === 0) {
        return [];
      }

      // Reconstruct the message parts
      const savedValue0 = sessionStorage.getItem("a") || authState.a;
      const savedValue1 = sessionStorage.getItem("b") || authState.b;

      if (!savedValue0 || !savedValue1) {
        console.error("Missing session storage values for decryption");
        return [];
      }

      const value = combineAndDecryptPrivateKey([
        decryptString(savedValue0),
        decryptString(savedValue1),
      ]);

      // Process messages in parallel
      const messagePromises = response.data.result.items.map(async (msg: any) => {
        try {
          const decryptedMessage = await decryptMessage(
            base64ToUint8Array(
              msg.sender === currentUserWalletAddress
                ? currentUserPublicKey || ""
                : receiversPublicKey || ""
            ),
            base64ToUint8Array(value),
            { ciphertext: msg.encryptedContent, nonce: msg.nonce }
          );

          const parts = parseMessage(decryptedMessage);
          const contactName = msg.sender === user?.walletAddress? "Me" :contactsState.contacts.find(
            (c) => c.walletAddress === msg.sender
          )?.name || shortenAddress(msg.sender);
         

          return {
            id: msg.id,
            content: msg.content,
            timestamp: new Date(msg.creationTime).toISOString(),
            direction: msg.sender === user?.walletAddress ? "outgoing" : "incoming",
            status: "delivered",
            sender: contactName,
            receiver: msg.receiver,
            parts: parts,
          };
        } catch (error) {
          console.error("Failed to decrypt message:", error);
          return {
            id: msg.id,
            content: "Failed to decrypt message",
            timestamp: new Date(msg.creationTime).toISOString(),
            direction: msg.sender === user?.walletAddress ? "outgoing" : "incoming",
            status: "error",
            sender: msg.sender,
            receiver: msg.receiver,
            parts: [],
          };
        }
      });

      // Wait for all messages to be processed
      const messages = await Promise.all(messagePromises);
      return messages;
    } catch (error) {
      console.error("Failed to fetch messages:", error);
      return rejectWithValue({
        message: "Failed to Get Messages",
        code: "MESSAGES_REQUEST_ERROR",
      });
    }
  }
);

// export const sendNewMessage = createAsyncThunk(
//   "messages/sendMessage",
//   async (message: Omit<Message, "id" | "timestamp">, { dispatch }) => {
//     const response = await sendMessage(message);
//     return response;
//   }
// );

const messagesSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<Message>) => {
        const existingMessageIndex = state.messages.findIndex(
    message => message.id === action.payload.id
  );
  
  if (existingMessageIndex === -1) {
    state.messages.push(action.payload);
  } else {
    // Optional: You could update the existing message here if needed
    // state.messages[existingMessageIndex] = action.payload;
    console.log("Message with this ID already exists, not adding again:", action.payload.id);
  }
    },
    updateMessage: (state, action: PayloadAction<Message>) => {
      const idx = state.messages.findIndex((m) => m.id === action.payload.id);
      if (idx !== -1) {
        state.messages[idx] = action.payload;
      }
    },
    messageReceived: (state, action: PayloadAction<Message>) => {
      state.messages.push(action.payload);
    },
    updateMessageStatus: (
      state,
      action: PayloadAction<{
        id: string;
        status: "delivered" | "read" | "sent" | "error";
      }>
    ) => {
      const message = state.messages.find((m) => m.id === action.payload.id);
      if (message) {
        message.status = action.payload.status;
      }
    },
    toggleAttachmentDrawer: (state) => {
      state.attachmentDrawerOpen = !state.attachmentDrawerOpen;
    },
    addMessagePart: (state, action: PayloadAction<MessagePart>) => {
      state.messageParts.push(action.payload);
    },
    clearMessageParts: (state) => {
      state.messageParts = [];
    },
    deleteMessagePart: (state, action: PayloadAction<number>) => {
      state.messageParts = state.messageParts.filter(
        (_, index) => index !== action.payload
      );
    },
     updateFailedMessage: (state, action: PayloadAction<{id: string}>) => {
      const message = state.messages.find(m => m.id === action.payload.id);
      if (message) {
        message.status = "error";
      }
    }  
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMessages.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.messages = action.payload;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to fetch messages";
      })
      // .addCase(sendNewMessage.fulfilled, (state, action) => {
      //   state.messages.push(action.payload);
      // })
      .addCase(fetchMessagesByContact.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.fetchMessagesByContact.loading = true;
        state.fetchMessagesByContact.error = "";
      })
      .addCase(fetchMessagesByContact.fulfilled, (state, action) => {
        state.status = "succeeded";
        // console.log("Fetched Messages:", JSON.stringify(action.payload));
        state.messages = action.payload || [];
        state.fetchMessagesByContact.loading = false;
        state.fetchMessagesByContact.error = "";
      })
      .addCase(fetchMessagesByContact.rejected, (state, action) => {
        state.status = "failed";
        state.fetchMessagesByContact.loading = false;
        // state.fetchMessagesByContact.error =
        //   action.payload?.message || "Failed to fetch messages by contact";
      })
      .addCase(sendNewMessage.pending, (state) => {
        state.sendStatus.loading = true;
        state.sendStatus.error = "";
      })
      .addCase(sendNewMessage.fulfilled, (state, action) => {
        state.sendStatus.loading = false;
        const idx = state.messages.findIndex(m => m.id === action.payload.id);
        if (idx !== -1) {
          state.messages[idx] = action.payload;
        }
      })
      .addCase(sendNewMessage.rejected, (state, action) => {
        state.sendStatus.loading = false;
        state.sendStatus.error = (action.payload as any)?.message || "Failed to send message";
        
        // Update the failed message status if we have the temp ID
        if ((action.payload as any)?.tempMessageId) {
          const message = state.messages.find(m => m.id === (action.payload as any).tempMessageId);
          if (message) {
            message.status = "error";
          }
        }
      })
      ;
  },
});

export const {
  messageReceived,
  updateMessageStatus,
  addMessage,
  updateMessage,
  toggleAttachmentDrawer,
  addMessagePart,
  clearMessageParts,
  deleteMessagePart,
} = messagesSlice.actions;
export default messagesSlice.reducer;
